import { useEffect, useState } from 'react';
import createContext from 'constate';
import { useAuth } from './AuthContext';
import useInactivity from '../hooks/useInactivity';
import { useQueryLocation } from '../hooks/useLocationApi';
import { useIsFirstAppRender } from '../hooks/useIsFirstAppRender';
import { PinScreenSetting, usePinScreenSetting } from '../hooks/usePinScreenSetting';
import { CashPaymentStatus, useCashPaymentStatus } from '../hooks/useCashPaymentStatus';
import { PaymentMethodDto } from '../../generated-api';
import { useQuickerLoginEnabled } from '../hooks/useFeatureFlagApi';
import { useIosInitiatedChangeUser } from '../hooks/useIosInitiatedChangeUser';

const nonLocationRoutes = ['locations', 'softpay']; // dette må vi få til bedre

const [PinScreenSettingsContextProvider, usePinScreenSettings] = createContext(() => {
  const currentLocationId = location.pathname.split('/')[1]!;
  const isNotALocation = nonLocationRoutes.includes(currentLocationId);
  const locationId = isNotALocation ? undefined : currentLocationId;
  const { data: favritLocation } = useQueryLocation(locationId);
  const { changeUser } = useAuth();
  const { getPinScreenSetting, setPinScreenSetting: setPinScreenSettingToStorage } = usePinScreenSetting();
  const [enableInactivityCheck, setEnableInactivityCheck] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const { isFirstRender, setIsFirstRender } = useIsFirstAppRender();
  const { cashPaymentStatus, clearCashPaymentStatus, startMonitoring, stopMonitoring } = useCashPaymentStatus();
  const { data: quickerLoginEnabled } = useQuickerLoginEnabled(favritLocation?.id);
  const { data: iosInitiatedChangeUser } = useIosInitiatedChangeUser();

  const configurePinScreenSetting = (setting: PinScreenSetting, metadata: string | undefined) => {
    if (setting === PinScreenSetting.afterInactivePeriod && metadata) {
      try {
        const msg = typeof metadata == 'object' ? metadata : JSON.parse(metadata);
        const parsedMetadata = msg as { seconds: string };
        const iSeconds = parseInt(parsedMetadata.seconds);
        setSeconds(iSeconds);
        setEnableInactivityCheck(true);
      } catch {
        setSeconds(0);
        setEnableInactivityCheck(false);
        return;
      }

      return;
    }

    if (setting === PinScreenSetting.afterEachSale) {
      setSeconds(2);
      setEnableInactivityCheck(false);
      return;
    }

    setSeconds(0);
    setEnableInactivityCheck(false);
  };

  useEffect(() => {
    const { setting, metadata } = getPinScreenSetting();
    configurePinScreenSetting(setting, metadata);
  }, [getPinScreenSetting]);

  useEffect(() => {
    const { showPinScreenOnStartup } = getPinScreenSetting();
    if (showPinScreenOnStartup && isFirstRender() && favritLocation?.id) {
      setIsFirstRender(false);
      changeUser(favritLocation?.id);
      return;
    }
  }, [changeUser, favritLocation?.id, getPinScreenSetting, isFirstRender, setIsFirstRender]);

  useEffect(() => {
    if (iosInitiatedChangeUser && favritLocation?.id && quickerLoginEnabled?.enabled) {
      sessionStorage.removeItem('ios_initiated_change_user');
      changeUser(favritLocation?.id);
    }
  }, [iosInitiatedChangeUser, favritLocation?.id, quickerLoginEnabled?.enabled, changeUser]);

  useEffect(() => {
    const { setting } = getPinScreenSetting();

    if (setting === PinScreenSetting.afterEachSale && cashPaymentStatus === CashPaymentStatus.CAPTURED) {
      setEnableInactivityCheck(true);
      stopMonitoring();
      clearCashPaymentStatus();
    }
  }, [cashPaymentStatus, clearCashPaymentStatus, getPinScreenSetting, stopMonitoring]);

  const onSetPinScreenSetting = (
    setting: PinScreenSetting,
    metadata: string | undefined,
    showPinScreenOnStartup: boolean
  ) => {
    setPinScreenSettingToStorage(setting, metadata, showPinScreenOnStartup);
    configurePinScreenSetting(setting, metadata);
    setIsFirstRender(false);
  };

  const initiateChangeUserOnCompletedSale = (paymentMethod?: PaymentMethodDto) => {
    const { setting } = getPinScreenSetting();

    if (setting === PinScreenSetting.afterEachSale) {
      const isCashPayment = paymentMethod?.paymentMethod === 'CASH';

      if (isCashPayment) {
        startMonitoring();
      } else {
        setEnableInactivityCheck(true);
      }
    }
  };

  const onChangeUser = () => {
    if (!favritLocation) return;
    changeUser(favritLocation?.id);
  };

  useInactivity({ enableInactivityCheck, timeout: seconds * 1000, handleInactive: onChangeUser });

  return {
    pinScreenSetting: getPinScreenSetting,
    setPinScreenSetting: onSetPinScreenSetting,
    initiateChangeUserOnCompletedSale,
  };
});

export { PinScreenSettingsContextProvider, usePinScreenSettings };
